import * as React from "react"
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from "lucide-react"
import { cn } from "@/lib/utils"

export interface ErrorAlertProps {
 type?: "success" | "error" | "warning" | "info"
 title?: string
 message?: string
 suggestion?: string
 onClose?: () => void
 className?: string
 icon?: React.ElementType
}

const typeConfig = {
 success: {
 icon: CheckCircle,
 bgColor: "bg-green-50 dark:bg-green-900/20",
 borderColor: "border-green-200 dark:border-green-800",
 iconColor: "text-green-600 dark:text-green-400",
 titleColor: "text-green-800 dark:text-green-200",
 textColor: "text-green-700 dark:text-green-300",
 suggestionBg: "bg-green-100 dark:bg-green-900/30",
 suggestionText: "text-green-800 dark:text-green-200",
 },
 error: {
 icon: AlertCircle,
 bgColor: "bg-red-50 dark:bg-red-900/20",
 borderColor: "border-red-200 dark:border-red-800",
 iconColor: "text-red-600 dark:text-red-400",
 titleColor: "text-red-800 dark:text-red-200",
 textColor: "text-red-700 dark:text-red-300",
 suggestionBg: "bg-red-100 dark:bg-red-900/30",
 suggestionText: "text-red-800 dark:text-red-200",
 },
 warning: {
 icon: AlertTriangle,
 bgColor: "bg-amber-50 dark:bg-amber-900/20",
 borderColor: "border-amber-200 dark:border-amber-800",
 iconColor: "text-amber-600 dark:text-amber-400",
 titleColor: "text-amber-800 dark:text-amber-200",
 textColor: "text-amber-700 dark:text-amber-300",
 suggestionBg: "bg-amber-100 dark:bg-amber-900/30",
 suggestionText: "text-amber-800 dark:text-amber-200",
 },
 info: {
 icon: Info,
 bgColor: "bg-blue-50 dark:bg-blue-900/20",
 borderColor: "border-blue-200 dark:border-blue-800",
 iconColor: "text-blue-600 dark:text-blue-400",
 titleColor: "text-blue-800 dark:text-blue-200",
 textColor: "text-blue-700 dark:text-blue-300",
 suggestionBg: "bg-blue-100 dark:bg-blue-900/30",
 suggestionText: "text-blue-800 dark:text-blue-200",
 },
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
 type = "error",
 title,
 message,
 suggestion,
 onClose,
 className,
 icon: CustomIcon,
}) => {
 const config = typeConfig[type]
 const Icon = CustomIcon || config.icon

 return (
 <div
 className={cn(
 "rounded-lg border-l-4 p-4 animate-in slide-in-from-top-2 duration-300",
 config.bgColor,
 config.borderColor,
 className
 )}
 >
 <div className="flex items-start">
 <div className="flex-shrink-0">
 <Icon className={cn("h-6 w-6", config.iconColor)} />
 </div>
 <div className="ml-3 flex-1">
 {title && (
 <h3 className={cn("text-sm font-bold mb-1", config.titleColor)}>{title}</h3>
 )}
 {message && (
 <p className={cn("text-sm mb-2", config.textColor)}>{message}</p>
 )}
 {suggestion && (
 <div className={cn("mt-2 p-2 rounded-md text-xs", config.suggestionBg, config.suggestionText)}>
 <strong className="font-semibold">💡 Sugestão:</strong> {suggestion}
 </div>
 )}
 </div>
 {onClose && (
 <button
 type="button"
 onClick={onClose}
 className={cn(
 "ml-3 flex-shrink-0 rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2",
 config.iconColor,
 "hover:bg-opacity-20 focus:ring-offset-gray-50 dark:focus:ring-offset-gray-900"
 )}
 aria-label="Fechar"
 >
 <X className="h-5 w-5" />
 </button>
 )}
 </div>
 </div>
 )
}

